import { useEffect, useRef, useState, forwardRef } from "react";
import { Button } from "./button";
import { Textarea } from "./textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./tooltip";
import { ControllerRenderProps } from "react-hook-form";
import { SubmissionPayload } from "@/lib/types/comms";
import { cn } from "@/lib/utils";
import { Microphone, MicrophoneSlash } from "@phosphor-icons/react";

const useAutosizeTextArea = (
  textAreaRef: HTMLTextAreaElement | null,
  value: string
) => {
  useEffect(() => {
    if (textAreaRef) {
      textAreaRef.style.height = "0px";
      const scrollHeight = textAreaRef.scrollHeight;

      // We then set the height directly, outside of the render loop
      // Trying to set this with state or a ref will product an incorrect value.
      textAreaRef.style.height = scrollHeight + "px";
    }
  }, [textAreaRef, value]);
};

const SpeechTextArea = forwardRef<
  HTMLTextAreaElement,
  ControllerRenderProps<SubmissionPayload, "body" | "subject"> & {
    className?: string;
    placeholder?: string | string[];
    onFocus?: React.FocusEventHandler<HTMLTextAreaElement>;
    onBlur?: React.FocusEventHandler<HTMLTextAreaElement>;
  }
>(({ placeholder, onFocus, onBlur, ...props }, ref) => {
  const [finalTranscript, setFinalTranscript] = useState("");
  const [interimTranscript, setInterimTranscript] = useState("");
  const [recognitionRunning, setRecognitionRunning] = useState(false);
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

  const recognitionRef = useRef<SpeechRecognition | null>(null);

  useAutosizeTextArea(
    textAreaRef.current,
    recognitionRunning
      ? interimTranscript
        ? interimTranscript
        : finalTranscript
      : finalTranscript
  );

  useEffect(() => {
    if (!("webkitSpeechRecognition" in window)) {
      return;
    }

    const recognition = new webkitSpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognitionRef.current = recognition;

    recognition.onresult = (event) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; ++i) {
        if (event.results[i].isFinal) {
          setFinalTranscript((prev) => prev + event.results[i][0].transcript);
          props.onChange(event.results[i][0].transcript);
        } else {
          interim += event.results[i][0].transcript;
        }
      }
      setInterimTranscript(interim);
    };

    recognition.onend = () => {
      setRecognitionRunning(false);
      setInterimTranscript("");
    };

    recognition.onerror = (event) => {
      if (event.error !== "aborted") {
        console.error("Error occurred in recognition: " + event.error);
      }
    };

    return () => {
      recognition.abort();
    };
  }, []);

  const handleMicClick = () => {
    if (recognitionRef.current) {
      if (!recognitionRunning) {
        recognitionRef.current.start();
        setRecognitionRunning(true);
      } else {
        recognitionRef.current.abort();
        setRecognitionRunning(false);
      }
    }
  };
  const isFirefox = navigator.userAgent.toLowerCase().indexOf("firefox") > -1;

  useEffect(() => {
    if (isFirefox) {
      console.warn("Speech recognition is not supported in Firefox.");
    }
  }, [isFirefox]);

  const placeholderRef = useRef<string>("");
  if (!placeholderRef.current) {
    if (Array.isArray(placeholder) && placeholder.length > 0) {
      placeholderRef.current =
        placeholder[Math.floor(Math.random() * placeholder.length)];
    } else if (typeof placeholder === "string") {
      placeholderRef.current = placeholder;
    }
  }
  const placeholderText = placeholderRef.current;

  return (
    <div
      className="mt-2"
      style={{ position: "relative", display: "inline-block" }}
    >
      <Textarea
        {...props}
        onFocus={onFocus}
        onBlur={(e) => {
          if (onBlur) onBlur(e);
        }}
        onChange={(e) => props.onChange(e)}
        value={
          recognitionRunning
            ? interimTranscript
              ? interimTranscript
              : finalTranscript
            : props.value
        }
        className={cn("pr-16", props.className ? props.className : "")}
        ref={ref}
        placeholder={placeholderText}
      >
        {interimTranscript && (
          <span className="text-muted-foreground">{interimTranscript}</span>
        )}
      </Textarea>
      <TooltipProvider delayDuration={100}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={handleMicClick}
              className="absolute rounded-md bottom-4 right-5"
            >
              {recognitionRunning && !isFirefox ? (
                <Microphone size={48} color={"#0000ff"} />
              ) : (
                <MicrophoneSlash size={48} />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">
            <span>
              {isFirefox
                ? "Unfortunately, speech recognition is not currently supported in Firefox."
                : "Start speech recognition"}
            </span>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
});

export default SpeechTextArea;
